/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import LuiInputBase from '@here/lui-base'
import scopedCss from 'lui-tab.scss'
import template from 'lui-tab.html'

/**
 * LUI Tab
 * @fires LuiTab#event:change
 * @description Event is fired when a Tab is changed eg selected
 */
export default class LuiTab extends LuiInputBase {
  static get is() {
    return 'lui-tab'
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  init() {
    this.style.width = 'auto'
    this._getField()
    requestAnimationFrame(() => {
      if (this.selected) this._selectTab()
      if (!this.selected) this._unselectTab()
    })
  }

  _getField() {
    this._field = this.closest('a, button')
    if (this._field) {
      this.removeAttribute('tabindex')
      this._field.classList.add('lui-reset-styles')
    }
  }

  onConnected() {
    this.setAttribute('role', 'menuitem')
    this.style.whiteSpace = 'nowrap'
    this.addEventListener('click', this._onClick)
    this.classList.add('lui-tabbar__item')
    if (!window.lui.usePolyfills && this.selected) this.selectedChanged()
  }

  onDisconnected() {
    this.removeEventListener('click', this._onClick)
  }

  selectedChanged(wasSelected) {
    if (this.selected && wasSelected !== '') {
      this._selectTab()
    }
    if (!this.selected) {
      this._unselectTab()
    }
    this.dispatchEvent(new Event('change', {bubbles: true}))
  }

  _onClick() {
    if (this.hasAttribute('manual')) {
      return
    }
    if (this.hasAttribute('semimanual')) {
      this._selectTab()
      return
    }
    this.selected = true
  }

  _selectTab() {
    if (!this.initialized) return
    const rect = this.getBoundingClientRect()
    this.withoutTransition(() => {
      this.style.width = rect.width + 'px'
      this.style.whiteSpace = 'nowrap'
    }).then(() => {
      this.toggleModifier('selected', true)
      this.setAttribute('aria-selected', 'true')
    })
  }

  _unselectTab() {
    this.withoutTransition(() => {
      this.toggleModifier('selected', false)
      this.setAttribute('aria-selected', 'false')
    })
  }

  get selected() {
    return this.hasAttribute('selected')
  }

  set selected(value) {
    if (value) {
      this.setAttribute('selected', '')
    } else {
      this.removeAttribute('selected')
    }
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-tabbar/lui-tab.js
// module id = 341
// module chunks = 0 1 6